import { Box, Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { ContractStaffing } from './contractStaffing';

import type { BudgetMetric } from '@/components/financials/metricReading';
import { formatFactorAs } from '@/components/financials/metricReading';
import { formatAmount } from '@/lib';

/**
 * The contract-metadata pieces the task panel and the rail's Contract insights
 * share (2026-10-01, the pilot's ask, so the two read alike): the Own Work
 * factor chip and the Own Work budget line. `FigureBlock`, the figure laid out
 * like a ring, went on 3 Nov: the hours cascade's contract figure, its last
 * wearer, is styled like the panel's Hours figure now.
 *
 * The rings and figures that gave this module its name — one contract's
 * distributed and allocated shares, its allocated revenue and uplift — were
 * replaced on both surfaces by the four KPI boxes in `ContractKpiBoxes` and
 * the hours cascade (2026-10-29, the designer's ask). See `contractStaffing.ts`
 * for the figures.
 */

/** The contract-metadata line's style: the billing method, factor and budget. */
const META_SX = {
  flex: 'none',
  fontFamily: 'var(--font-family-mono)',
  fontSize: 11,
  color: 'var(--fg-3)',
  whiteSpace: 'nowrap',
} as const;

/**
 * The uplift SET on the contract (CALC-20), for comparison with the forecasted
 * one: "factor ×2.80" (or "margin 64%"). Contract metadata, so it sits with the
 * contract's billing method in the rail and inside the contract picker in the
 * task panel (2026-10-01, the pilot's ask), not under the factor figure.
 */
export const ContractOwnFactor = ({
  metric,
  ownFactor,
}: {
  metric: BudgetMetric;
  ownFactor: number;
}) => {
  const { t } = useTranslation(['pmPlanningPanel', 'pmCommon']);
  return (
    // A small fog chip, as a span: it also sits inside the contract picker's
    // button, where a div is not allowed.
    <Chip
      component="span"
      size="small"
      color="fog"
      label={t('contractStaffing.OwnFactor', {
        metric: t(`pmCommon:metric.${metric}`).toLowerCase(),
        value: formatFactorAs(metric, ownFactor),
      })}
      data-automation-id="contract-own-factor"
      // A lighter ground than the fog chip's fog-20 (3 Nov, Mette's note).
      sx={{ flex: 'none', bgcolor: 'var(--color-fog-10)' }}
    />
  );
};

/** The contract's Own Work amount (CALC-12's own share) — metadata, beside the factor. */
export const ContractOwnWorkBudget = ({ staffing }: { staffing: ContractStaffing }) => {
  const { t } = useTranslation('pmPlanningPanel');
  return (
    <Box component="span" data-automation-id="contract-own-work-budget" sx={META_SX}>
      {t('contractStaffing.OwnWorkBudget', { amount: formatAmount(staffing.ownAmount) })}
    </Box>
  );
};
